import { useMemo } from 'react'

import useSharedCollection from '../firebase/useSharedCollection'
import resolveStatusDisplay from '../helpers/statusConfig/resolveStatusDisplay'
import { ADRESS_STATUS_LABELS } from '../helpers/adressHelpers/adressStatusLabels'
import { PERSONA_STATUS_LABELS } from '../helpers/personas/personaStatusLabels'

/**
 * De weergavelaag van deze organisatie over beide statuslijsten, uit een
 * `useSettings()`-uitkomst die je al hebt.
 *
 * Pak deze wanneer het scherm `useSettings()` al aanroept — een tweede aanroep
 * zet een tweede stel Firestore-listeners op hetzelfde document op. Wikkel hem
 * zelf in `useMemo` op de twee configvelden, zodat afnemers die op identiteit
 * vergelijken niet elke render opnieuw gaan rekenen.
 *
 * @param {object} [settings] - Uitkomst van `useSettings()`
 * @returns {{adress: object, persona: object}}
 */
const statusDisplaysFrom = (settings) => ({
    adress: resolveStatusDisplay(ADRESS_STATUS_LABELS, settings && settings.adressStatusConfig),
    persona: resolveStatusDisplay(PERSONA_STATUS_LABELS, settings && settings.personaStatusConfig),
})

/**
 * Hetzelfde, maar met een eigen lees-abonnement eronder.
 *
 * Pak deze wanneer het scherm de instellingen verder niet nodig heeft.
 *
 * **Leest via `useSharedCollection` en niet via `useSettings()`.** Die laatste
 * opent drie listeners (CompagnyMeta, Subscriptions, OutputEffects) terwijl
 * deze hook er twee velden van gebruikt. `useSharedCollection` opent er één per
 * collectie × organisatie en deelt die met alle afnemers, met opruiming zodra
 * de laatste vertrekt. Dat maakt het verschil op een pagina die dit component
 * in een lus rendert: `DashboardPersonaFilter` staat binnen elke analysekaart,
 * en bij de grootste organisatie zijn dat 24 kaarten — met `useSettings` was
 * dat 72 listeners en ruim 300 documentlezingen voor twee tekstvelden.
 *
 * Roep hem nog steeds niet PER RIJ aan: het huishoudenoverzicht rendert 394
 * rijen, en een gedeelde listener is goedkoop maar de weergave uitrekenen niet.
 * Reken hem één keer uit en geef hem als prop mee — daarom heeft
 * `AdressStatusSelect` een `display`-prop.
 *
 * AI-CONTEXT: See docs/decisions.md#adr-032
 * AI-CONTEXT: See docs/features/tenant-status-and-column-config.md
 *
 * @returns {{adress: object, persona: object}}
 */
const useStatusDisplays = () => {
    const compagny = useSharedCollection('CompagnyMeta')
    const meta = Array.isArray(compagny) ? compagny[0] : undefined

    return useMemo(
        () => statusDisplaysFrom({
            adressStatusConfig: meta && meta.AdressStatusConfig,
            personaStatusConfig: meta && meta.PersonaStatusConfig,
        }),
        [meta],
    )
}

export { statusDisplaysFrom }
export default useStatusDisplays
